import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './api';
import type {
  Survey,
  SurveyDetail,
  SurveyDashboard,
  SurveyInvitation,
  SurveyResponseItem,
  SurveyResponseDetail,
  HubSpotContactList,
  CreateSurveyRequest,
  UpdateSurveyQuestionsRequest,
  SendSurveyRequest,
  SendSurveyResponse,
} from '@clareefai/types';

export const surveyApi = createApi({
  reducerPath: 'surveyApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Survey', 'SurveyResponse', 'SurveyDashboard', 'SurveyInvitation'],
  endpoints: (builder) => ({
    getSurveyDashboard: builder.query<SurveyDashboard, void>({
      query: () => '/vendors/me/surveys/dashboard',
      providesTags: ['SurveyDashboard'],
    }),

    getSurveys: builder.query<Survey[], void>({
      query: () => '/vendors/me/surveys',
      providesTags: ['Survey'],
    }),

    getSurvey: builder.query<SurveyDetail, string>({
      query: (surveyId) => `/vendors/me/surveys/${surveyId}`,
      providesTags: (_result, _error, surveyId) => [{ type: 'Survey', id: surveyId }],
    }),

    createSurvey: builder.mutation<SurveyDetail, CreateSurveyRequest>({
      query: (body) => ({
        url: '/vendors/me/surveys',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Survey', 'SurveyDashboard'],
    }),

    updateSurveyQuestions: builder.mutation<SurveyDetail, { surveyId: string; data: UpdateSurveyQuestionsRequest }>({
      query: ({ surveyId, data }) => ({
        url: `/vendors/me/surveys/${surveyId}/questions`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { surveyId }) => [{ type: 'Survey', id: surveyId }, 'Survey'],
    }),

    getSurveyInvitations: builder.query<SurveyInvitation[], string>({
      query: (surveyId) => `/vendors/me/surveys/${surveyId}/invitations`,
      providesTags: (_result, _error, surveyId) => [{ type: 'SurveyInvitation', id: surveyId }],
    }),

    sendSurvey: builder.mutation<SendSurveyResponse, { surveyId: string; data: SendSurveyRequest }>({
      query: ({ surveyId, data }) => ({
        url: `/vendors/me/surveys/${surveyId}/send`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { surveyId }) => ['SurveyDashboard', { type: 'SurveyInvitation', id: surveyId }],
    }),

    getSurveyResponses: builder.query<SurveyResponseItem[], string | undefined>({
      query: (surveyId) => ({
        url: '/vendors/me/surveys/responses',
        params: surveyId ? { surveyId } : undefined,
      }),
      providesTags: ['SurveyResponse'],
    }),

    getSurveyResponseDetail: builder.query<SurveyResponseDetail, string>({
      query: (responseId) => `/vendors/me/surveys/responses/${responseId}`,
      providesTags: (_result, _error, responseId) => [{ type: 'SurveyResponse', id: responseId }],
    }),

    getHubSpotContactLists: builder.query<HubSpotContactList[], void>({
      query: () => '/vendors/me/surveys/hubspot/contact-lists',
    }),

    copySurveyLink: builder.mutation<{ link: string }, string>({
      query: (surveyId) => ({
        url: `/vendors/me/surveys/${surveyId}/copy-link`,
        method: 'POST',
      }),
    }),

    deleteSurvey: builder.mutation<void, string>({
      query: (surveyId) => ({
        url: `/vendors/me/surveys/${surveyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Survey', 'SurveyDashboard', 'SurveyResponse'],
    }),
  }),
});

export const {
  useGetSurveyDashboardQuery,
  useGetSurveysQuery,
  useGetSurveyQuery,
  useGetSurveyInvitationsQuery,
  useCreateSurveyMutation,
  useUpdateSurveyQuestionsMutation,
  useSendSurveyMutation,
  useGetSurveyResponsesQuery,
  useGetSurveyResponseDetailQuery,
  useGetHubSpotContactListsQuery,
  useCopySurveyLinkMutation,
  useDeleteSurveyMutation,
} = surveyApi;
